import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@shared-kernel/primary/shared/shadcn/ui/card";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";

interface CardListItemProps {
  avatar: ReactNode;
  content: ReactNode;
  trailing?: ReactNode;
  footer?: ReactNode;
  /** Déclenche une action au clic sur le corps de la carte. Exclusif avec `to`. */
  onAction?: () => void;
  /** Mène à une route au clic sur le corps de la carte. Exclusif avec `onAction`. */
  to?: string;
  /** Posé sur l'élément cliquable, là où un test ira chercher la carte. */
  actionTestId?: string;
  className?: string;
}

// Une seule allure pour « ce corps de carte se clique », que ce soit vers une action ou vers une
// route. Le pied reste dehors : il peut contenir ses propres contrôles, et un lecteur audio
// imbriqué dans un bouton ne se lancerait jamais sans déclencher la carte.
const INTERACTIVE_BODY_CLASSES = "block w-full cursor-pointer rounded-md text-left transition-colors hover:bg-muted/50";

export const CardListItem = ({ avatar, content, trailing, footer, onAction, to, actionTestId, className }: CardListItemProps) => {
  const body = (
    <div className="flex items-center gap-4">
      {avatar}
      <div className="min-w-0 flex-1">{content}</div>
      {trailing && <div className="shrink-0 pr-2">{trailing}</div>}
    </div>
  );

  return (
    <Card className={cn("border-border bg-card", className)}>
      <CardContent className="p-2">
        {to ? (
          <Link to={to} data-testid={actionTestId} className={INTERACTIVE_BODY_CLASSES}>
            {body}
          </Link>
        ) : onAction ? (
          <button onClick={onAction} data-testid={actionTestId} className={INTERACTIVE_BODY_CLASSES}>
            {body}
          </button>
        ) : (
          body
        )}
        {/* Rattaché au corps par un filet plutôt que posé en dessous : on doit voir de quelle ligne
            ce pied dépend. */}
        {footer && <div className="mt-2 border-t border-border/60 px-2 pb-1 pt-2">{footer}</div>}
      </CardContent>
    </Card>
  );
};
